import { CloseCircleFilled, DownOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons';
import { Checkbox, Dropdown, Empty, Input, Segmented, Tree } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Фильтр с галочками над таблицей.
 *
 * Поле показывает не сами значения, а «Статус: 2/6» — так оно не растёт
 * от числа отмеченного. В открытой панели: «Все / Выбрано» (второе — чтобы
 * быстро увидеть и снять отмеченное в длинном списке), поиск и список.
 */

/** Цвета меток antd → цвет текста в списке (тот же оттенок, что у метки). */
const TEXT_COLOR: Record<string, string> = {
  green: '#389e0d',
  success: '#389e0d',
  blue: '#0958d9',
  processing: '#0958d9',
  geekblue: '#1d39c4',
  cyan: '#08979c',
  purple: '#531dab',
  magenta: '#c41d7f',
  red: '#cf1322',
  error: '#cf1322',
  volcano: '#d4380d',
  orange: '#d46b08',
  warning: '#d48806',
  gold: '#d48806',
  lime: '#5b8c00',
  default: 'rgba(0, 0, 0, 0.65)',
};

export const textColorOf = (color: string | undefined): string | undefined =>
  color ? (TEXT_COLOR[color] ?? color) : undefined;

type View = 'all' | 'selected';

/** Поле фильтра и выпадающая панель — общие для списка и дерева. */
function FilterShell({
  placeholder,
  selected,
  total,
  width,
  onClear,
  children,
}: {
  placeholder: string;
  selected: number;
  total: number;
  width: number;
  onClear: () => void;
  children: (state: { view: View; search: string }) => ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('all');
  const [search, setSearch] = useState('');

  const panel = (
    <div className="tms-filter-panel" style={{ minWidth: Math.max(width, 260) }}>
      <Segmented<View>
        block
        size="small"
        value={view}
        onChange={setView}
        options={[
          {
            value: 'all',
            label: (
              <span className="tms-filter-tab">
                {t('Все')} <span className="tms-filter-count">{total}</span>
              </span>
            ),
          },
          {
            value: 'selected',
            label: (
              <span className="tms-filter-tab">
                {t('Выбрано')} <span className="tms-filter-count">{selected}</span>
              </span>
            ),
          },
        ]}
      />
      <Input
        allowClear
        size="small"
        variant="filled"
        prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.35)' }} />}
        placeholder={t('Поиск...')}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        style={{ margin: '8px 0' }}
      />
      <div className="tms-filter-list">{children({ view, search: search.trim().toLowerCase() })}</div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSearch('');
          setView('all');
        }
      }}
      trigger={['click']}
      placement="bottomLeft"
      popupRender={() => panel}
    >
      <button
        type="button"
        className={`tms-filter-field${open ? ' is-open' : ''}${selected > 0 ? ' has-value' : ''}`}
        style={{ width }}
      >
        <span className="tms-filter-field-text">
          {selected > 0 ? (
            <>
              {placeholder}: <b>{selected}</b>/{total}
            </>
          ) : (
            <span className="tms-filter-placeholder">{placeholder}</span>
          )}
        </span>
        {selected > 0 && (
          <CloseCircleFilled
            className="tms-filter-clear"
            onClick={(event) => {
              event.stopPropagation();
              onClear();
            }}
          />
        )}
        <span className="tms-filter-arrow">{open ? <UpOutlined /> : <DownOutlined />}</span>
      </button>
    </Dropdown>
  );
}

export interface CheckOption<Value> {
  value: Value;
  label: string;
  /** Цвет текста (имя цвета метки antd или hex) — для статусов. */
  color?: string;
}

/** Фильтр-список с галочками. Ничего не отмечено — фильтра нет. */
export function CheckSelect<Value extends string | number>({
  value,
  onChange,
  options,
  placeholder,
  width = 190,
}: {
  value: Value[];
  onChange: (value: Value[]) => void;
  options: Array<CheckOption<Value>>;
  placeholder: string;
  width?: number;
}) {
  const { t } = useTranslation();
  const toggle = (item: Value): void =>
    onChange(value.includes(item) ? value.filter((v) => v !== item) : [...value, item]);

  return (
    <FilterShell
      placeholder={placeholder}
      selected={value.length}
      total={options.length}
      width={width}
      onClear={() => onChange([])}
    >
      {({ view, search }) => {
        const visible = options.filter(
          (option) =>
            (view === 'all' || value.includes(option.value)) &&
            (!search || option.label.toLowerCase().includes(search)),
        );
        if (visible.length === 0) {
          return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Ничего не найдено')} />;
        }
        return visible.map((option) => (
          <label key={String(option.value)} className="tms-filter-option">
            <Checkbox checked={value.includes(option.value)} onChange={() => toggle(option.value)} />
            <span style={{ color: textColorOf(option.color) }}>{option.label}</span>
          </label>
        ));
      }}
    </FilterShell>
  );
}

export interface TreeOption {
  value: number;
  title: string;
  children?: TreeOption[];
}

/**
 * Фильтр-дерево с галочками (подразделения). Отмеченный родитель означает
 * «вместе со всеми вложенными» — в значение уходит только он, как и
 * фильтрует сервер.
 */
export function TreeCheckFilter({
  value,
  onChange,
  treeData,
  placeholder,
  width = 220,
}: {
  value: number[];
  onChange: (value: number[]) => void;
  treeData: TreeOption[];
  placeholder: string;
  width?: number;
}) {
  const { t } = useTranslation();

  const { parentOf, titleOf, all } = useMemo(() => {
    const parents = new Map<number, number | null>();
    const titles = new Map<number, string>();
    const walk = (nodes: TreeOption[], parent: number | null): void => {
      for (const node of nodes) {
        parents.set(node.value, parent);
        titles.set(node.value, node.title);
        walk(node.children ?? [], node.value);
      }
    };
    walk(treeData, null);
    return { parentOf: parents, titleOf: titles, all: [...titles.keys()] };
  }, [treeData]);

  // Отмечено в дереве — выбранные узлы и все их потомки.
  const checked = useMemo(() => {
    const roots = new Set(value);
    return all.filter((id) => {
      for (let p: number | null | undefined = id; p != null; p = parentOf.get(p)) {
        if (roots.has(p)) return true;
      }
      return false;
    });
  }, [value, all, parentOf]);

  /** Из всех отмеченных оставляем верхние — потомки подразумеваются. */
  const toRoots = (keys: number[]): number[] => {
    const set = new Set(keys);
    return keys.filter((id) => {
      const parent = parentOf.get(id);
      return parent == null || !set.has(parent);
    });
  };

  const filterTree = (nodes: TreeOption[], search: string): DataNode[] =>
    nodes.flatMap((node) => {
      const children = filterTree(node.children ?? [], search);
      const match = !search || node.title.toLowerCase().includes(search);
      return match || children.length > 0
        ? [{ key: node.value, title: node.title, children: match ? toNodes(node.children ?? []) : children }]
        : [];
    });
  const toNodes = (nodes: TreeOption[]): DataNode[] =>
    nodes.map((node) => ({ key: node.value, title: node.title, children: toNodes(node.children ?? []) }));

  return (
    <FilterShell
      placeholder={placeholder}
      selected={value.length}
      total={all.length}
      width={width}
      onClear={() => onChange([])}
    >
      {({ view, search }) => {
        if (view === 'selected') {
          const rows = value.filter((id) => !search || (titleOf.get(id) ?? '').toLowerCase().includes(search));
          if (rows.length === 0) {
            return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Ничего не выбрано')} />;
          }
          return rows.map((id) => (
            <label key={id} className="tms-filter-option">
              <Checkbox checked onChange={() => onChange(value.filter((v) => v !== id))} />
              <span>{titleOf.get(id)}</span>
            </label>
          ));
        }
        const nodes = filterTree(treeData, search);
        if (nodes.length === 0) {
          return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Ничего не найдено')} />;
        }
        return (
          <Tree
            checkable
            selectable={false}
            defaultExpandAll
            // Поиск раскрывает найденное: дерево пересоздаётся под строку поиска.
            key={search}
            treeData={nodes}
            checkedKeys={checked}
            onCheck={(keys) => {
              const list = (Array.isArray(keys) ? keys : keys.checked).map(Number);
              // Дерево знает только показанные поиском узлы — отмеченные
              // среди скрытых сохраняем как были.
              const shown = new Set<number>();
              const collect = (items: DataNode[]): void =>
                items.forEach((item) => {
                  shown.add(Number(item.key));
                  collect(item.children ?? []);
                });
              collect(nodes);
              const hidden = checked.filter((id) => !shown.has(id));
              onChange(toRoots([...hidden, ...list]));
            }}
          />
        );
      }}
    </FilterShell>
  );
}
